import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import PatientSidebar from "./PatientSidebar";
import SECTION_META from "../helper/sectionMeta";
import SECTIONS from "../sections";
import { SheetCountsProvider, useSheetCounts } from "./sheetCounts";
import UnsavedCardsGuard from "./UnsavedCardsGuard";
import "../styles/patientShell.scss";
import "../styles/sections.scss";
/* Loaded LAST so its @media (max-width: …) overrides win the cascade at the
   tablet/phone tiers. Desktop rules above stay authoritative — this file has
   nothing that applies above 1024px. */
import "../styles/phrResponsive.scss";

/**
 * The three-column section body of the patient screen: sidebar | content |
 * editor. Source: assembled section boards (e.g. Diagnosis 1982:12525) read via
 * Dev Mode MCP — Content Host 1528px, columns 260 / flex(832) / 340, 24px gaps.
 *
 * Controlled: the active section lives in PatientDetails so the per-section
 * title can render above the breadcrumb, matching Figma. Built sections come
 * from the registry; the rest fall back to placeholder slots.
 *
 * ZY-10350 — cross-section unsaved editor cards survive section switches.
 * Every editor-equipped section's Editor is mounted ONCE for the shell's
 * lifetime; CSS visibility swaps show just the active one. SheetCountsProvider
 * holds a shell-level `{sectionKey: openCount}` registry each editor writes
 * into via useSheetCountRegistration (plumbed through useSheetStack for the
 * ten standard sections, and directly for Anthropometric + Patient details).
 * Sidebar reads it to render the per-section badge; the leave-page warning
 * reads it to list sections with pending work.
 */
const PatientShell = (props) => (
  <SheetCountsProvider>
    <PatientShellBody {...props} />
    {/* beforeunload + in-app history-nav guard (ZY-10350). Lives inside
        the provider so it sees the same counts the sidebar + editors
        write into. Renders nothing until a navigation attempt fires. */}
    <UnsavedCardsGuard />
  </SheetCountsProvider>
);

PatientShell.propTypes = {
  active: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  /** The loaded patient record, handed to every section. */
  patient: PropTypes.object,
  /** Currently-selected appointment id — forwarded to Content + Editor
   *  so sections that scope writes to an appointment (Lab
   *  investigations) can stamp it. Sections that don't care ignore it. */
  appointmentId: PropTypes.number,
  /** A section key whose add form should open once its list loads (Create Rx's
   *  "+ Add …"), or null. */
  autoOpenAdd: PropTypes.string,
  /** Called when that section has consumed it. */
  onAutoOpenAdd: PropTypes.func,
};

const PatientShellBody = ({ active, onSelect, patient, appointmentId, autoOpenAdd, onAutoOpenAdd }) => {
  // Panel state is local: it is a viewing preference for this visit, not
  // something the URL or the record should carry.
  const [railCollapsed, setRailCollapsed] = useState(false);
  const meta = SECTION_META[active] || { name: "" };
  const section = SECTIONS[active];
  const Content = section && section.Content;

  // Some sections (the Lifestyle cards) are content-only — no 340px editor rail.
  // For those the content spans the full width and the aside is dropped.
  const contentOnly = !!(section && section.contentOnly);

  // Fixed list of every section that ships an Editor — mounted once each,
  // kept across section switches so an in-flight write survives when the
  // reviewer navigates away and back (ZY-10350). Visibility is swapped
  // below via `is-active`; React keeps the hidden editors' state intact.
  const editorEntries = useMemo(
    () =>
      Object.keys(SECTIONS)
        .filter((key) => SECTIONS[key] && SECTIONS[key].Editor && !SECTIONS[key].contentOnly)
        .map((key) => ({ key, Editor: SECTIONS[key].Editor })),
    [],
  );

  // ZY-10312 — content-only sections (Food preference / Daily schedule /
  // Progress notes) have no editor rail, so their editable state lives
  // entirely in the <Content>. Mount all three in parallel inside
  // `.ph-content` and swap visibility with `.ph-content-slot.is-active`
  // so a tab bounce (Daily schedule → CC → Daily schedule) doesn't
  // remount Content and discard the reviewer's in-progress edits.
  // Mirrors the editor-slot pattern above; only applies to contentOnly
  // because non-contentOnly sections keep their critical state in the
  // (already persistent) editor slot.
  const contentOnlyEntries = useMemo(
    () =>
      Object.keys(SECTIONS)
        .filter((key) => SECTIONS[key] && SECTIONS[key].Content && SECTIONS[key].contentOnly)
        .map((key) => ({ key, Content: SECTIONS[key].Content })),
    [],
  );

  // On-demand editors (Lab investigations): the 340px column is hidden and the
  // content takes its width until the active section's own editor stack has at
  // least one open card. Reads from the shell-level SheetCountsProvider (which
  // every editor publishes into via useSheetCountRegistration) so the hide
  // rule is per-section — a Chief complaints card left open while the reviewer
  // visits Lab investigations doesn't force Lab's rail open.
  const counts = useSheetCounts();
  const onDemand = !!(section && section.editorOnDemand);
  const activeCount = counts[active] || 0;
  const editorHidden = onDemand && activeCount === 0;

  const activeEditorEntry = editorEntries.find((e) => e.key === active);
  const hasActiveEditor = !!(activeEditorEntry || (section && section.Editor));

  return (
    // The id is the scroll anchor for a `?section=` deep link — the sections sit
    // below the header card and overview row, i.e. off-screen on arrival.
    <div
      id="ph-sections"
      className={`ph-shell-body${contentOnly ? " is-content-only" : ""}${
        railCollapsed ? " is-rail-collapsed" : ""
      }${editorHidden ? " is-editor-hidden" : ""}`}
    >
      <PatientSidebar
        active={active}
        onSelect={onSelect}
        collapsed={railCollapsed}
        onToggle={() => setRailCollapsed((c) => !c)}
      />

      <section className="ph-content" aria-label={`${meta.name} content`}>
        {/* Content-only sections (Food preference / Daily schedule /
            Progress notes) — mounted in parallel, visibility swapped
            with `is-active`. Keeps the reviewer's in-progress edits
            alive across tab bounces (ZY-10312). */}
        {contentOnlyEntries.map(({ key, Content: CO }) => (
          <div
            key={key}
            className={`ph-content-slot${key === active ? " is-active" : ""}`}
            aria-hidden={key !== active}
          >
            <CO
              patient={patient}
              appointmentId={appointmentId}
              autoOpenAdd={!!autoOpenAdd && autoOpenAdd === key}
              onAutoOpenAdd={onAutoOpenAdd}
            />
          </div>
        ))}
        {/* Non-contentOnly sections keep their old render path: only the
            active section's Content is mounted. Their editable state
            lives in the (persistent) editor slot, so Content remounts
            on tab switch are fine. */}
        {!contentOnly && Content ? (
          <Content
            patient={patient}
            appointmentId={appointmentId}
            /* Create Rx's "+ Add …": only the section it named, only once — see
               sections/common/useAutoOpenAdd. Sections without an add flow
               ignore it. */
            autoOpenAdd={!!autoOpenAdd && autoOpenAdd === active}
            onAutoOpenAdd={onAutoOpenAdd}
          />
        ) : null}
        {!Content && !contentOnly && (
          <div className="ph-slot-empty">
            <p>{meta.name} content will appear here.</p>
            <span>Sections are being built one at a time.</span>
          </div>
        )}
      </section>

      {/* ZY-10312 — the aside is mounted UNCONDITIONALLY, including on
          content-only sections (Food preference / Daily schedule /
          Progress notes). The earlier `{!contentOnly && ...}` guard
          unmounted the entire column when the reviewer visited one of
          those three, which took every editor's React state with it
          and silently discarded any unsaved cards in other sections.
          `.ph-shell-body.is-content-only > .ph-editor { display: none }`
          now hides the column visually (same recipe `is-editor-hidden`
          uses for on-demand Lab investigations) so the content still
          takes the freed width while every editor stays alive. */}
      <aside className="ph-editor" aria-label={`${meta.name} editor`}>
        {/* Every section's Editor is mounted here. Only the active one is
            visible (`.ph-editor-slot.is-active`); the rest sit under
            `display:none` with their state intact so sheets survive a
            section switch. Content-only sections are filtered out of
            editorEntries above because they ship no Editor. */}
        {editorEntries.map(({ key, Editor }) => (
          <div
            key={key}
            className={`ph-editor-slot${key === active ? " is-active" : ""}`}
            aria-hidden={key !== active}
          >
            <Editor patient={patient} appointmentId={appointmentId} />
          </div>
        ))}
        {/* Placeholder when the active section has no Editor yet (sections
            still being built, or content-only sections while their
            column is CSS-hidden). Only renders when no slot above
            claimed it. */}
        {!hasActiveEditor && (
          <div className="ph-slot-empty is-editor">
            <p>Editor panel</p>
          </div>
        )}
      </aside>
    </div>
  );
};

PatientShellBody.propTypes = PatientShell.propTypes;

export default PatientShell;
